import type {
  ArmOrigin,
  BatchDecision,
  CostEstimate,
  ExplorationDecisionLog,
  ExplorationTerminationReason,
  ExplorationView,
  JudgedCandidate,
  PendingRatingArm,
  PosteriorWeight,
  StructuredQuery,
  TernaryRating,
} from "@shared/labs/sourcing";
import {
  ChevronDown,
  Coins,
  Download,
  FileText,
  Layers,
  Loader2,
  Minus,
  RotateCcw,
  SlidersHorizontal,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  UserCheck,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useToast } from "@/hooks/use-toast";
import { rpcClient } from "@/lib/rpc";
import { cn } from "@/lib/utils";
import { BriefStep } from "./brief-step";
import { CandidateCard } from "./candidate-card";
import { CreateSetDialog } from "./create-set-dialog";
import { downloadJson } from "./lib";
import { EMPTY_LA_SCOPE, type LaScopeSelection } from "./model-picker";
import { type FlowProject, RunProjectActions } from "./projects/run-project-actions";
import { QueryInterpretation } from "./query-summary";
import { RecentExplorations } from "./recent-explorations";
import { ShareLinkButton } from "./share-link-button";

const POLL_INTERVAL_MS = 1000;
/** Page-budget slider (the thoroughness lever; ≈ pages × 25 profiles explored). The
 *  server clamps to the same [MIN, MAX] step grid. */
const MIN_PAGES = 10;
const MAX_PAGES = 40;
const PAGES_STEP = 5;
const DEFAULT_PAGES = 20;
/** Slider step stops, for the notches under the track (10, 15, …, 40). */
const PAGE_TICKS = Array.from(
  { length: (MAX_PAGES - MIN_PAGES) / PAGES_STEP + 1 },
  (_, i) => MIN_PAGES + i * PAGES_STEP,
);
/** Level-Assessment evaluation sets are capped at 20 candidates. */
const MAX_SET = 20;

function defaultSetName(brief: string): string {
  const snippet = brief.replace(/\s+/g, " ").trim().slice(0, 60);
  return snippet ? `Sourcing — ${snippet}` : "Sourcing";
}

/**
 * The autonomous explore loop. The recruiter describes a search and sets a page
 * budget; the bandit then runs on its own — probing facet "arms", LLM-judging the
 * full deduped page of each, retiring low-yield arms and branching the winners —
 * with NO manual rating, stopping early once it stops finding fresh matches. The UI
 * is pure progress → results: a live header (profiles explored, pages spent) over a
 * shortlist that fills in, ranked by the judge's fit score, each with its "why".
 */
export function ExploreFlow({
  initialExplorationId,
  project,
}: {
  initialExplorationId?: string;
  project?: FlowProject;
} = {}) {
  const { toast } = useToast();
  // A project session starts from the project's brief + LA scope (editable —
  // iterating the brief per session is the point; the project keeps its own).
  const [brief, setBrief] = useState(project?.brief ?? "");
  const [laScope, setLaScope] = useState<LaScopeSelection>(
    project
      ? { modelId: project.laModelId, disciplineId: project.laDisciplineId, level: project.laLevel }
      : EMPTY_LA_SCOPE,
  );
  const [query, setQuery] = useState<StructuredQuery | null>(null);
  const [pages, setPages] = useState(DEFAULT_PAGES);
  // Human-in-the-loop: when on, the run pauses each batch to blend the recruiter's
  // ternary ratings with the auto-judge (RLHF), weighting the human above the judge.
  const [humanRating, setHumanRating] = useState(false);
  const [view, setView] = useState<ExplorationView | null>(null);
  const [busy, setBusy] = useState(false);
  // Start in the reopening state when we arrive via a shared deep-link, so the very
  // first render shows the spinner instead of briefly flashing the "describe the job"
  // create flow (which would also fire a labs-only `list` call the viewer can't make).
  const [reopening, setReopening] = useState(Boolean(initialExplorationId));

  // Selection for "create an LA evaluation set from these". Seeded to the top-20
  // by FIT (results arrive already FIT-ranked); auto-reseeds as the list grows
  // until the recruiter manually edits it, then leaves their choice alone.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [setDialogOpen, setSetDialogOpen] = useState(false);
  const selectionTouched = useRef(false);

  const idRef = useRef<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const results = view?.results ?? [];
  // Depend on the stable `view.results` reference (a new array only arrives when a
  // poll advances the view), and skip while empty so we never loop on a fresh `[]`.
  useEffect(() => {
    if (selectionTouched.current || !view?.results?.length) return;
    setSelectedIds(new Set(view.results.slice(0, MAX_SET).map((c) => c.id)));
  }, [view?.results]);

  const toggleSelect = (id: string) => {
    selectionTouched.current = true;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < MAX_SET) next.add(id);
      return next;
    });
  };

  const selectedCandidates = useMemo(
    () => results.filter((c) => selectedIds.has(c.id)),
    [results, selectedIds],
  );
  const atCap = selectedIds.size >= MAX_SET;

  useEffect(() => {
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
      idRef.current = null;
    };
  }, []);

  // Shared deep-link (`?exploration=…`): open that run's results on mount.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reopen is stable enough; key off the id so it runs once per shared link
  useEffect(() => {
    if (initialExplorationId) void reopen(initialExplorationId);
  }, [initialExplorationId]);

  function stopPolling() {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }

  function poll(id: string) {
    rpcClient.labs
      .sourcingExplorationGet({ explorationId: id })
      .then((v) => {
        if (idRef.current !== id) return;
        setView(v);
        if (v.status === "done" || v.status === "error") {
          setBusy(false);
          stopPolling();
          if (v.status === "error") {
            toast({
              title: "Falha na exploração",
              description: v.errorMessage ?? "A exploração terminou com erro.",
              variant: "destructive",
            });
          }
          return;
        }
        // Keep polling on every non-terminal status. `awaiting_input` is a PARKED run
        // (the recruiter must rate) — not "busy", but we keep the poll alive so the
        // transition back to running after they submit is picked up automatically.
        setBusy(v.status !== "awaiting_input");
        pollRef.current = setTimeout(() => poll(id), POLL_INTERVAL_MS);
      })
      .catch((err) => {
        if (idRef.current !== id) return;
        setBusy(false);
        stopPolling();
        toast({
          title: "Falha na exploração",
          description: err instanceof Error ? err.message : "Erro inesperado",
          variant: "destructive",
        });
      });
  }

  async function start() {
    if (!query) return;
    selectionTouched.current = false;
    setSelectedIds(new Set());
    setBusy(true);
    setView(null);
    try {
      const { explorationId } = await rpcClient.labs.sourcingExploreStart({
        query,
        pages,
        laModelId: laScope.modelId ?? undefined,
        laDisciplineId: laScope.disciplineId ?? undefined,
        laLevel: laScope.level ?? undefined,
        projectId: project?.id,
        ratingMode: humanRating ? "human_in_loop" : undefined,
      });
      idRef.current = explorationId;
      poll(explorationId);
    } catch (err) {
      setBusy(false);
      toast({
        title: "Falha ao iniciar a exploração",
        description: err instanceof Error ? err.message : "Erro inesperado",
        variant: "destructive",
      });
    }
  }

  // Submit one round's ratings (the review feed fires this per round, as each completes)
  // and resume the run. Free (no Apify/LLM). Polling is already live on `awaiting_input`,
  // so it picks up the transition back to running → the next pause (or done) automatically.
  async function rate(ratings: { candidateId: string; rating: TernaryRating }[]) {
    const id = idRef.current;
    if (!id) return;
    setBusy(true);
    try {
      await rpcClient.labs.sourcingExploreRate({ explorationId: id, ratings });
      if (!pollRef.current) poll(id); // defensively re-arm if the timer had stopped
    } catch (err) {
      setBusy(false);
      toast({
        title: "Falha ao enviar avaliações",
        description: err instanceof Error ? err.message : "Erro inesperado",
        variant: "destructive",
      });
    }
  }

  // "Concluir agora": finish a parked human-in-the-loop run early and keep the shortlist
  // so far. Free (no Apify/LLM) — CAS `awaiting_input → done` server-side; a poll then
  // renders the final results view. A lost CAS (the worker moved it) just re-polls.
  async function finalize() {
    const id = idRef.current;
    if (!id) return;
    try {
      await rpcClient.labs.sourcingExploreFinalize({ explorationId: id });
      if (!pollRef.current) poll(id);
    } catch (err) {
      toast({
        title: "Não foi possível concluir agora",
        description: err instanceof Error ? err.message : "Erro inesperado",
        variant: "destructive",
      });
      poll(id); // refresh the true status
    }
  }

  function reset() {
    stopPolling();
    idRef.current = null;
    setView(null);
    setQuery(null);
    setBrief("");
    setLaScope(EMPTY_LA_SCOPE);
    setPages(DEFAULT_PAGES);
    setHumanRating(false);
    setBusy(false);
    setReopening(false);
    selectionTouched.current = false;
    setSelectedIds(new Set());
  }

  // Reopen a saved exploration: load its persisted state (the shortlist is free —
  // no new Apify spend) and, if it's still running, resume polling it.
  async function reopen(id: string) {
    stopPolling();
    setQuery(null);
    setView(null);
    setBrief("");
    setReopening(true);
    selectionTouched.current = false;
    setSelectedIds(new Set());
    idRef.current = id;
    try {
      const v = await rpcClient.labs.sourcingExplorationGet({ explorationId: id });
      if (idRef.current !== id) return;
      setView(v);
      if (v.status === "done" || v.status === "error") {
        setBusy(false);
      } else {
        setBusy(true);
        pollRef.current = setTimeout(() => poll(id), POLL_INTERVAL_MS);
      }
    } catch (err) {
      if (idRef.current !== id) return;
      idRef.current = null;
      toast({
        title: "Falha ao reabrir a exploração",
        description: err instanceof Error ? err.message : "Erro inesperado",
        variant: "destructive",
      });
    } finally {
      if (idRef.current === id || idRef.current === null) setReopening(false);
    }
  }

  if (reopening) {
    return (
      <section className="space-y-5">
        <ExploringView reopening />
      </section>
    );
  }

  // A live or reopened view takes precedence over the create flow. For a human-in-the-loop
  // run this is the FLOWING review feed — rate a trickle of candidates while the search
  // probes ahead — with the shortlist growing alongside; for an auto run it's the classic
  // progress → results.
  if (view) {
    const done = view.status === "done";
    const running = !done && view.status !== "error";
    const hitl = view.ratingMode === "human_in_loop";
    const pending = view.pending ?? [];
    // The feed shows while a HITL run is live and has candidates buffered to rate. The full
    // select-and-push-to-Level-Assessment shortlist appears once the run finishes.
    const showFeed = hitl && running && pending.length > 0;
    return (
      <section className="space-y-5">
        <ProgressHeader view={view} running={running} />
        <BriefPanel brief={view.brief} laScope={view.laScope} />
        <ExplorationStrip view={view} running={running} />

        {showFeed && (
          <ReviewFeed
            view={view}
            pending={pending}
            awaiting={view.status === "awaiting_input"}
            onSubmitRound={rate}
            onFinalize={finalize}
          />
        )}

        {/* Live HITL: a collapsible peek at the shortlist so far. */}
        {showFeed && <PartialShortlist results={view.results} />}

        {/* Auto runs, and the finished HITL run: the full shortlist with LA-set selection. */}
        {!showFeed && view.results.length > 0 && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold">
                  {done ? "Melhores candidatos" : "Candidatos avaliados até agora"}
                </h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  <span className="tabular-nums">{selectedCandidates.length}</span> / {MAX_SET}{" "}
                  selecionados para avaliação de nível — marque ou desmarque abaixo.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2.5">
                <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                  {view.results.length} avaliados
                </span>
                <Button
                  size="sm"
                  onClick={() => setSetDialogOpen(true)}
                  disabled={selectedCandidates.length === 0}
                >
                  <Layers /> Avaliar no Level Assessment
                  {selectedCandidates.length > 0 && (
                    <span className="ml-1 tabular-nums opacity-80">
                      ({selectedCandidates.length})
                    </span>
                  )}
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadJson(view)}>
                  <Download /> Baixar JSON
                </Button>
                <ShareLinkButton explorationId={view.id} kind="explore" />
              </div>
            </div>

            <CreateSetDialog
              open={setDialogOpen}
              onOpenChange={setSetDialogOpen}
              candidates={selectedCandidates}
              prefillScope={laScope}
              defaultName={defaultSetName(brief)}
            />

            {view.results.map((c, i) => (
              <ResultCard
                key={c.id}
                candidate={c}
                rank={i + 1}
                selected={selectedIds.has(c.id)}
                selectionDisabled={atCap}
                onToggleSelect={() => toggleSelect(c.id)}
              />
            ))}
          </div>
        )}

        {running && view.results.length === 0 && !showFeed && <ExploringView />}

        {done && (
          <div className="flex items-center justify-between border-t border-border pt-4">
            <span className="text-xs text-muted-foreground">
              {view.results.length} candidatos avaliados · {view.pagesUsed}{" "}
              {view.pagesUsed === 1 ? "página" : "páginas"}
            </span>
            <div className="flex items-center gap-2">
              <RunProjectActions view={view} />
              <Button variant="outline" onClick={reset}>
                <RotateCcw />
                Nova exploração
              </Button>
            </div>
          </div>
        )}
      </section>
    );
  }

  if (!query) {
    return (
      <div className="space-y-2">
        <BriefStep
          brief={brief}
          setBrief={setBrief}
          onParsed={setQuery}
          laScope={laScope}
          setLaScope={setLaScope}
          enableLevel
        />
        <RecentExplorations onReopen={reopen} />
      </div>
    );
  }

  return (
    <section className="space-y-5">
      <StartPanel
        query={query}
        pages={pages}
        setPages={setPages}
        humanRating={humanRating}
        setHumanRating={setHumanRating}
        busy={busy}
        onStart={start}
        onBack={() => setQuery(null)}
      />
      {busy && <ExploringView />}
    </section>
  );
}

/**
 * The pre-flight review: the parsed brief rendered as a legible fact sheet so the
 * recruiter can confirm what we understood BEFORE spending. The interpretation
 * (role, seniority/level, experience window, location, sector, must-haves vs.
 * differentials, and the bandit's starting approaches) all come straight off the
 * already-parsed `query` — no extra fetch, no spend. The only paid lever is the
 * page-budget slider; "Iniciar exploração" is the single action that costs money.
 */
function StartPanel({
  query,
  pages,
  setPages,
  humanRating,
  setHumanRating,
  busy,
  onStart,
  onBack,
}: {
  query: StructuredQuery;
  pages: number;
  setPages: (p: number) => void;
  humanRating: boolean;
  setHumanRating: (v: boolean) => void;
  busy: boolean;
  onStart: () => void;
  onBack: () => void;
}) {
  const [cost, setCost] = useState<CostEstimate | null>(null);
  const [estimating, setEstimating] = useState(false);

  // Free cost estimate (Apify + judging) for the chosen page budget; re-priced on change.
  useEffect(() => {
    let cancelled = false;
    setEstimating(true);
    rpcClient.labs
      .sourcingExploreEstimate({ pages })
      .then((c) => {
        if (!cancelled) setCost(c);
      })
      .catch(() => {
        if (!cancelled) setCost(null);
      })
      .finally(() => {
        if (!cancelled) setEstimating(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pages]);

  const arms = query.arms ?? [];

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <QueryInterpretation
        query={query}
        eyebrow="Revise antes de começar"
        heading="Confira o que entendemos do briefing"
      />

      {/* The bandit's starting plan — collapsed by default. */}
      {arms.length > 0 && <ExplorePlan arms={arms} />}

      {/* Page budget — the one paid lever — with its live cost/size estimate. */}
      <div className="mt-5 border-t border-border pt-5">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-6">
          {/* slider + step notches */}
          <div className="min-w-0 flex-1">
            <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              profundidade da busca
            </div>
            <Slider
              value={[pages]}
              min={MIN_PAGES}
              max={MAX_PAGES}
              step={PAGES_STEP}
              disabled={busy}
              onValueChange={([p]) => setPages(p)}
              aria-label="Profundidade da busca em páginas"
            />
            <div className="mt-1.5 flex justify-between px-px">
              {PAGE_TICKS.map((t) => (
                <span key={t} className="flex flex-col items-center gap-1">
                  <span className="h-1.5 w-px bg-border" />
                  <span className="font-mono text-[9px] tabular-nums text-muted-foreground">
                    {t}
                  </span>
                </span>
              ))}
            </div>
          </div>

          {/* cost + size estimate, to the side */}
          <div className="flex items-center gap-5 sm:border-l sm:border-border sm:pl-6">
            <div className="text-right">
              <div className="font-mono text-sm font-semibold tabular-nums">
                {cost ? cost.pages : "—"}
              </div>
              <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground">
                páginas
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono text-sm font-semibold tabular-nums">
                {cost ? `≤ ${cost.maxProfiles}` : "—"}
              </div>
              <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground">
                perfis
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <Coins size={14} className="text-amber-500" />
              <div className="font-mono text-2xl font-semibold leading-none tabular-nums">
                <span className="text-muted-foreground">até $</span>
                {cost ? cost.usd.toFixed(2) : estimating ? "…" : "0.00"}
              </div>
            </div>
          </div>
        </div>

        <p className="mt-2 max-w-xl text-xs leading-relaxed text-muted-foreground">
          Define o teto de páginas analisadas ({MIN_PAGES}–{MAX_PAGES}). Mais páginas = mais
          candidatos explorados, a um custo proporcional — a busca pode terminar antes do teto se
          parar de encontrar bons perfis.
        </p>
      </div>

      {/* Human-in-the-loop toggle — same paid path/firewall; it just inserts rating pauses. */}
      <button
        type="button"
        onClick={() => setHumanRating(!humanRating)}
        disabled={busy}
        aria-pressed={humanRating}
        className={cn(
          "mt-4 flex w-full items-start gap-3 rounded-lg border p-3.5 text-left transition-colors disabled:opacity-60",
          humanRating ? "border-primary/50 bg-primary/5" : "border-border hover:bg-muted/40",
        )}
      >
        <UserCheck
          size={17}
          className={cn("mt-0.5 shrink-0", humanRating ? "text-primary" : "text-muted-foreground")}
        />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 text-sm font-medium">
            Avaliar candidatos durante a busca
          </span>
          <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
            Você avalia um candidato de cada vez (👍 / 😐 / 👎) enquanto a busca continua trazendo
            mais — sem pausas nem listas longas. Suas notas guiam, em tempo real, quais veios
            aprofundar, com mais peso que o avaliador automático.
          </span>
        </span>
        <span
          className={cn(
            "relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition-colors",
            humanRating ? "bg-primary" : "bg-muted-foreground/30",
          )}
        >
          <span
            className={cn(
              "absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all",
              humanRating ? "left-[18px]" : "left-0.5",
            )}
          />
        </span>
      </button>

      <div className="mt-4 flex items-center gap-2">
        <Button variant="outline" onClick={onBack} disabled={busy}>
          <SlidersHorizontal /> Editar
        </Button>
        <Button onClick={onStart} disabled={busy}>
          {busy ? <Loader2 className="animate-spin" /> : <Sparkles />}
          Iniciar exploração
        </Button>
      </div>
    </div>
  );
}

/** One labelled cell of the parsed-brief fact sheet. */
/**
 * "Como vamos explorar" — the bandit's starting approaches, collapsed by default.
 * Each arm is one filter combination the search will try; it then concentrates on
 * whichever surface good profiles (and mutates them), so this is a starting plan,
 * not a fixed list. Read-only: the recruiter shapes it via "Editar", not here.
 */
function ExplorePlan({ arms }: { arms: NonNullable<StructuredQuery["arms"]> }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4 overflow-hidden rounded-lg border border-border">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left"
      >
        <span className="min-w-0">
          <span className="block text-xs font-semibold text-foreground">Como vamos explorar</span>
          <span className="block truncate text-[11px] text-muted-foreground">
            A busca roda sozinha e se ajusta enquanto encontra bons perfis
          </span>
        </span>
        <span className="inline-flex shrink-0 items-center gap-1.5">
          <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground tabular-nums">
            {arms.length} {arms.length === 1 ? "abordagem" : "abordagens"}
          </span>
          <ChevronDown
            size={15}
            className={cn("text-muted-foreground transition-transform", open && "rotate-180")}
          />
        </span>
      </button>
      {open && (
        <ol className="flex flex-col gap-px border-t border-border bg-border">
          {arms.map((arm, i) => {
            const label = arm.label ?? arm.title;
            const title = arm.title && arm.title !== arm.label ? arm.title : null;
            return (
              <li
                key={`${arm.label ?? ""}|${arm.title}`}
                className="flex items-start gap-3 bg-card px-3 py-2.5"
              >
                <span className="mt-px font-mono text-[11px] tabular-nums text-muted-foreground">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="min-w-0">
                  <span className="block text-[13px] font-medium text-foreground">{label}</span>
                  {title && (
                    <span className="block truncate text-[11px] text-muted-foreground">
                      {title}
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function ProgressHeader({ view, running }: { view: ExplorationView; running: boolean }) {
  const label = running ? "Explorando e avaliando candidatos…" : "Concluído";
  const pct = Math.min(100, Math.round((view.pagesUsed / Math.max(1, view.budgetPages)) * 100));
  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-sm font-medium">
          {running && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          {label}
        </span>
        <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground tabular-nums">
          {view.candidatesExplored} avaliados · {view.pagesUsed}/{view.budgetPages} págs
        </span>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-emerald-500/70" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/** One probed approach, aggregated across rounds: its legible facets, the distinct
 *  judged candidates it surfaced, and their mean FIT — the realized "aproveitamento". */
interface Approach {
  chips: string[];
  count: number;
  meanFit: number;
}

/** Roll the per-round frontier arms up into distinct approaches (the unified explore
 *  loop pools into `rounds` and writes no separate harvest). An arm reappears across
 *  rounds as the frontier persists, so we group by its facet signature, dedupe the
 *  candidates it surfaced, and score it by the mean judged FIT — best yield first. */
function topApproaches(rounds: ExplorationView["rounds"]): Approach[] {
  const byArm = new Map<string, { chips: string[]; ids: Set<string>; sum: number }>();
  for (const r of rounds) {
    for (const arm of r.arms) {
      const key = arm.facetChips.join("|");
      let g = byArm.get(key);
      if (!g) {
        g = { chips: arm.facetChips, ids: new Set(), sum: 0 };
        byArm.set(key, g);
      }
      for (const c of arm.candidates) {
        if (!c.judgement || g.ids.has(c.id)) continue;
        g.ids.add(c.id);
        g.sum += c.judgement.score;
      }
    }
  }
  return Array.from(byArm.values())
    .filter((g) => g.ids.size > 0)
    .map((g) => ({ chips: g.chips, count: g.ids.size, meanFit: g.sum / g.ids.size }))
    .sort((a, b) => b.meanFit - a.meanFit);
}

// ─────────────────────────────────────────────────────────────────────────────
// Exploration MAP — reconstruct the search as a journey. Each round is a column of
// arm nodes (sized by candidates surfaced, colored by mean FIT); productive arms
// branch/carry into the next round, low-yield ones are dropped, survivors flow into
// the final pool. A compass rail (the learned posterior) explains what steered it.
//
// REAL vs INFERRED lineage: runs now persist each arm's real lineage on the round records
// (`armKey` / `origin` / `parentKey` / `dormantReason`), so the map draws the TRUE journey —
// real branch/carry edges, a distinct "nova categoria" node for LLM-injected arms, and the
// real reason an arm stopped. Legacy rows (pre-lineage) lack `armKey`; for those we fall
// back to inferring the links from round-over-round facet overlap, and label them as such.
// ─────────────────────────────────────────────────────────────────────────────

type ArmRole = "seed" | "carry" | "branch" | "probe" | "explore";

interface MapArm {
  key: string;
  round: number;
  sig: string;
  chips: string[];
  title: string;
  /** Distinct judged candidates this arm surfaced. */
  count: number;
  meanFit: number;
  /** Parent arm node key (null = seed / injected / unattached probe). */
  parent: string | null;
  role: ArmRole;
  /** Surfaced before the final round but never carried/branched forward. */
  pruned: boolean;
  /** True when the lineage is REAL (persisted), false when inferred (legacy fallback). */
  real: boolean;
  /** The real reason the arm went dormant after this pull (real lineage only). */
  dormantReason?: "lowYield" | "smallPage" | "depthCap" | null;
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

function jaccard(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 && b.size === 0) return 1;
  let inter = 0;
  for (const x of Array.from(a)) if (b.has(x)) inter += 1;
  return inter / (a.size + b.size - inter);
}

/** Build the arm nodes from the REAL persisted lineage (`armKey`/`origin`/`parentKey`/
 *  `dormantReason`): one node per (round, armKey), with true branch/carry edges, a
 *  distinct `explore` node for LLM-injected categories, and the real dormancy reason. */
function buildRealArms(rounds: ExplorationView["rounds"]): MapArm[] {
  const parentArmKeyOf = new Map<string, string | undefined>(); // node.key → parent armKey
  const perRound: MapArm[][] = [];
  rounds.forEach((r, ri) => {
    const arms: MapArm[] = [];
    for (const arm of r.arms) {
      const armKey = arm.armKey;
      if (!armKey) continue;
      const ids = new Set<string>();
      let sum = 0;
      for (const c of arm.candidates) {
        if (!c.judgement || ids.has(c.id)) continue;
        ids.add(c.id);
        sum += c.judgement.score;
      }
      const origin = arm.origin ?? "seed";
      // Show every node that surfaced someone, plus dry INJECTED categories (the
      // "we went looking somewhere new and it was empty" event is worth showing).
      if (ids.size === 0 && origin !== "explore") continue;
      const key = `${ri}:${armKey}`;
      parentArmKeyOf.set(key, arm.parentKey);
      arms.push({
        key,
        round: ri,
        sig: armKey,
        chips: arm.facetChips,
        title: arm.facetChips[0] ?? "Sem título",
        count: ids.size,
        meanFit: ids.size ? sum / ids.size : 0,
        parent: null,
        role: origin === "explore" ? "explore" : "seed",
        pruned: false,
        real: true,
        dormantReason: arm.dormantReason ?? null,
      });
    }
    perRound.push(arms);
  });

  const all = perRound.flat();
  const bySig = new Map<string, MapArm[]>();
  for (const n of all) {
    const list = bySig.get(n.sig);
    if (list) list.push(n);
    else bySig.set(n.sig, [n]);
  }
  const latestPrior = (sig: string, round: number): MapArm | null =>
    (bySig.get(sig) ?? []).filter((p) => p.round < round).sort((a, b) => b.round - a.round)[0] ??
    null;

  for (const node of all) {
    const carry = latestPrior(node.sig, node.round); // same arm probed again ⇒ carried forward
    if (carry) {
      node.parent = carry.key;
      node.role = "carry";
      continue;
    }
    if (node.role === "explore") continue; // injected category: an entry point, no parent
    const parentArmKey = parentArmKeyOf.get(node.key);
    if (parentArmKey) {
      const parent = latestPrior(parentArmKey, node.round);
      if (parent) {
        node.parent = parent.key;
        node.role = "branch";
      }
    }
  }

  // Pruned = went dormant after this pull AND never reappeared (didn't carry forward).
  for (const node of all) {
    const reappears = (bySig.get(node.sig) ?? []).some((p) => p.round > node.round);
    node.pruned = node.dormantReason != null && !reappears;
  }
  return all;
}

/** Reconstruct the arm nodes — one per (round, facet-signature) — with realized yield
 *  and an INFERRED lineage (legacy fallback for rows persisted before lineage was stored):
 *  `carry` = same signature recurs, `branch` = refines a prior arm (strong facet overlap),
 *  `probe` = a new angle, `seed` = round 1. */
function buildInferredArms(rounds: ExplorationView["rounds"]): MapArm[] {
  const perRound: MapArm[][] = [];
  rounds.forEach((r, ri) => {
    const bySig = new Map<string, { chips: string[]; ids: Set<string>; sum: number }>();
    for (const arm of r.arms) {
      const sig = arm.facetChips.join("|");
      let g = bySig.get(sig);
      if (!g) {
        g = { chips: arm.facetChips, ids: new Set(), sum: 0 };
        bySig.set(sig, g);
      }
      for (const c of arm.candidates) {
        if (!c.judgement || g.ids.has(c.id)) continue;
        g.ids.add(c.id);
        g.sum += c.judgement.score;
      }
    }
    const arms: MapArm[] = [];
    for (const [sig, g] of Array.from(bySig.entries())) {
      if (g.ids.size === 0) continue;
      arms.push({
        key: `${ri}:${sig}`,
        round: ri,
        sig,
        chips: g.chips,
        title: g.chips[0] ?? "Sem título",
        count: g.ids.size,
        meanFit: g.sum / g.ids.size,
        parent: null,
        role: ri === 0 ? "seed" : "probe",
        pruned: false,
        real: false,
      });
    }
    perRound.push(arms);
  });

  const all = perRound.flat();
  for (let ri = 1; ri < perRound.length; ri += 1) {
    const priors = perRound.slice(0, ri).flat();
    for (const node of perRound[ri] ?? []) {
      const carry = priors.filter((p) => p.sig === node.sig).sort((a, b) => b.round - a.round)[0];
      if (carry) {
        node.parent = carry.key;
        node.role = "carry";
        continue;
      }
      const ns = new Set(node.chips);
      let best: MapArm | null = null;
      let bestScore = 0;
      for (const p of priors) {
        const score = jaccard(new Set(p.chips), ns) + (p.title === node.title ? 0.25 : 0);
        if (score > bestScore) {
          bestScore = score;
          best = p;
        }
      }
      // Strong overlap ⇒ a refinement of that arm; otherwise a fresh probe.
      if (best && bestScore >= 0.34) {
        node.parent = best.key;
        node.role = "branch";
      }
    }
  }

  const lastRound = perRound.length - 1;
  const parents = new Set(all.map((a) => a.parent).filter((k): k is string => Boolean(k)));
  for (const a of all) a.pruned = a.round !== lastRound && !parents.has(a.key);
  return all;
}

/** Use the REAL persisted lineage when the run stored it (any arm carries `armKey`),
 *  else fall back to inferring the links from facet overlap (legacy rows). Exported for
 *  unit testing the lineage reconstruction. */
export function buildExplorationArms(rounds: ExplorationView["rounds"]): MapArm[] {
  const hasLineage = rounds.some((r) => r.arms.some((a) => Boolean(a.armKey)));
  return hasLineage ? buildRealArms(rounds) : buildInferredArms(rounds);
}

// Map geometry (px) — mirrors the Claude Design layout.
const NODE_W = 204;
const GAP_X = 108;
const GAP_Y = 14;
const TOP_PAD = 30;
const POOL_W = 188;
const POOL_H = 128;
const PAD_L = 18;

const nodeHeight = (count: number) => 62 + clamp((count - 8) / 17, 0, 1) * 40;
const edgeWidth = (count: number) => 2 + clamp((count - 8) / 17, 0, 1) * 3;
const isWeak = (fit: number) => fit < 0.16;

function bezier(fx: number, fy: number, tx: number, ty: number): string {
  const dx = (tx - fx) * 0.5;
  return `M ${fx} ${fy} C ${fx + dx} ${fy}, ${tx - dx} ${ty}, ${tx} ${ty}`;
}

/** Color a node/edge by mean FIT: weak yield reads amber, healthy yield emerald with
 *  intensity scaling — the "aproveitamento" gradient. */
function fitFill(fit: number): { fill: string; border: string; text: string } {
  const norm = clamp((fit - 0.1) / 0.22, 0, 1);
  if (isWeak(fit)) {
    return {
      fill: `color-mix(in srgb, var(--cx-amber) ${Math.round(9 + norm * 10)}%, var(--cx-card))`,
      border: "color-mix(in srgb, var(--cx-amber) 44%, transparent)",
      text: "var(--cx-amber)",
    };
  }
  return {
    fill: `color-mix(in srgb, var(--cx-em-stroke) ${Math.round(10 + norm * 28)}%, var(--cx-card))`,
    border: `color-mix(in srgb, var(--cx-em-stroke) ${Math.round(34 + norm * 26)}%, transparent)`,
    text: "var(--cx-em)",
  };
}

function roleLabel(a: MapArm): string {
  if (a.pruned) return "encerrado";
  return {
    seed: "início",
    carry: "manteve",
    branch: "ramificou",
    probe: "sonda",
    explore: "nova categoria",
  }[a.role];
}

/** Real reason an arm went dormant (real lineage only). */
const DORMANCY_WHY: Record<string, string> = {
  lowYield: "Encerrada: o rendimento novo caiu abaixo do piso — fronteira esgotada.",
  smallPage: "Encerrada: a página retornou poucos perfis — oferta esgotada.",
  depthCap: "Encerrada: atingiu o limite de profundidade do arm.",
};

function whyText(a: MapArm): string {
  if (a.real) {
    if (a.pruned) return DORMANCY_WHY[a.dormantReason ?? ""] ?? "Encerrada.";
    if (a.role === "explore")
      return "Nova categoria injetada pela exploração dirigida (LLM) — fora do conjunto inicial.";
    if (a.role === "branch")
      return "Ramificou de um arm rico que começava a esgotar — exploração local.";
    if (a.role === "carry") return "A mesma busca seguiu rendendo — mantida na fronteira.";
    return "Semente inicial da fronteira.";
  }
  // Legacy rows — links inferred from facet overlap.
  if (a.pruned)
    return "Inferência: ausente na rodada seguinte — sem rendimento novo, foi descartada.";
  if (a.role === "branch")
    return "Inferência: refina um arm produtivo da rodada anterior — ramificação.";
  if (a.role === "carry")
    return "Inferência: a mesma assinatura reaparece na rodada seguinte — a fronteira persistiu.";
  if (a.role === "probe") return "Sonda nova: ângulo testado sem ascendência clara.";
  return "Semente inicial da fronteira.";
}

interface MapNode extends MapArm {
  x: number;
  y: number;
  w: number;
  h: number;
  cx0: number;
  cx1: number;
  cy: number;
}
interface MapEdge {
  id: string;
  d: string;
  stroke: string;
  w: number;
  dash: string;
  op: number;
}
interface MapLayout {
  W: number;
  H: number;
  nodes: MapNode[];
  edges: MapEdge[];
  labels: { t: string; left: number }[];
  pool: { x: number; y: number; cx0: number; cy: number };
}

/** Lay the rounds out as left→right columns feeding a terminal pool; each column is
 *  vertically centered and sorted best-yield-first, with bezier edges for the inferred
 *  lineage, a faded stub for pruned arms, and final-round arms flowing into the pool. */
function layoutExplorationMap(arms: MapArm[], nRounds: number): MapLayout {
  const cols: MapArm[][] = Array.from({ length: nRounds }, () => []);
  for (const a of arms) cols[a.round]?.push(a);
  for (const c of cols) c.sort((x, y) => y.meanFit - x.meanFit);
  const colH = cols.map(
    (c) => c.reduce((s, a) => s + nodeHeight(a.count), 0) + GAP_Y * Math.max(0, c.length - 1),
  );
  const maxH = Math.max(...colH, POOL_H, 1);
  const X = (i: number) => PAD_L + i * (NODE_W + GAP_X);
  const idx = new Map<string, MapNode>();
  const nodes: MapNode[] = [];
  cols.forEach((c, ci) => {
    let y = TOP_PAD + (maxH - (colH[ci] ?? 0)) / 2;
    for (const a of c) {
      const h = nodeHeight(a.count);
      const n: MapNode = {
        ...a,
        x: X(ci),
        y,
        w: NODE_W,
        h,
        cx0: X(ci),
        cx1: X(ci) + NODE_W,
        cy: y + h / 2,
      };
      idx.set(a.key, n);
      nodes.push(n);
      y += h + GAP_Y;
    }
  });
  const poolX = X(nRounds);
  const poolY = TOP_PAD + (maxH - POOL_H) / 2;
  const pool = { x: poolX, y: poolY, cx0: poolX, cy: poolY + POOL_H / 2 };

  const edges: MapEdge[] = [];
  for (const n of nodes) {
    if (!n.parent) continue;
    const p = idx.get(n.parent);
    if (!p) continue;
    edges.push({
      id: `e${n.key}`,
      d: bezier(p.cx1, p.cy, n.cx0, n.cy),
      stroke: isWeak(n.meanFit) ? "var(--cx-amber)" : "var(--cx-em-stroke)",
      w: edgeWidth(n.count),
      dash: "0",
      op: 0.9,
    });
  }
  for (const n of nodes) {
    if (n.round !== nRounds - 1) continue;
    const strong = n.meanFit >= 0.2;
    edges.push({
      id: `p${n.key}`,
      d: bezier(n.cx1, n.cy, pool.cx0, pool.cy),
      stroke: strong ? "var(--cx-em-stroke)" : "var(--cx-amber)",
      w: strong ? 4 : 2,
      dash: strong ? "0" : "5 5",
      op: strong ? 0.95 : 0.7,
    });
  }
  for (const n of nodes) {
    if (!n.pruned) continue;
    edges.push({
      id: `x${n.key}`,
      d: `M ${n.cx1} ${n.cy} c 26 2 40 18 58 32`,
      stroke: "var(--cx-fg3)",
      w: 1.5,
      dash: "2 5",
      op: 0.5,
    });
  }
  const labels = cols.map((_, i) => ({ t: `Rodada ${i + 1}`, left: X(i) }));
  labels.push({ t: "Pool final", left: poolX });
  return { W: poolX + POOL_W + PAD_L, H: maxH + TOP_PAD + 12, nodes, edges, labels, pool };
}

/**
 * The exploration map — the expanded body of "Como exploramos". A compass rail (the
 * learned posterior, what steers the search across runs), then a horizontal map of the
 * rounds: arm nodes sized by candidates and colored by FIT, branch/carry edges and
 * pruned stubs (inferred), feeding the final pool. Clicking a node reveals its filters
 * and why it advanced or was dropped. Ends with the page-budget receipt.
 */
function ExplorationMap({
  view,
  learned,
  running,
}: {
  view: ExplorationView;
  learned: PosteriorWeight[];
  running: boolean;
}) {
  const [sel, setSel] = useState<string | null>(null);
  const arms = useMemo(() => buildExplorationArms(view.rounds), [view.rounds]);
  const lineageReal = useMemo(() => arms.some((a) => a.real), [arms]);
  const layout = useMemo(
    () => layoutExplorationMap(arms, view.rounds.length),
    [arms, view.rounds.length],
  );
  const selected = sel ? (arms.find((a) => a.key === sel) ?? null) : null;

  const budgetLine = running
    ? `Em andamento · ${view.pagesUsed} de ${view.budgetPages} páginas usadas até agora.`
    : view.pagesUsed < view.budgetPages
      ? `Custo · ${view.pagesUsed} de ${view.budgetPages} páginas — parou cedo ao esgotar novos perfis, gastou menos que o orçamento.`
      : `Custo · ${view.pagesUsed} de ${view.budgetPages} páginas — usou todo o orçamento.`;

  return (
    <div
      className={cn(
        "flex flex-col gap-4 border-t border-border px-4 py-4",
        // Map color tokens (light defaults + dark overrides), scoped to this panel —
        // the SVG/positioned nodes need computed colors that plain Tailwind can't express.
        "[--cx-card:#ffffff] [--cx-border:rgba(15,25,35,0.11)] [--cx-border-soft:rgba(15,25,35,0.06)] [--cx-fg:#0f1923] [--cx-fg3:rgba(15,25,35,0.45)] [--cx-brand:#f4364c] [--cx-em:#047857] [--cx-em-stroke:#059669] [--cx-rose:#be123c] [--cx-rose-stroke:#e11d48] [--cx-amber:#b45309]",
        "dark:[--cx-card:#15191f] dark:[--cx-border:rgba(255,255,255,0.13)] dark:[--cx-border-soft:rgba(255,255,255,0.07)] dark:[--cx-fg:#f3f4f6] dark:[--cx-fg3:rgba(243,244,246,0.45)] dark:[--cx-em:#34d399] dark:[--cx-em-stroke:#34d399] dark:[--cx-rose:#fb7185] dark:[--cx-rose-stroke:#fb7185] dark:[--cx-amber:#fbbf24]",
      )}
    >
      {/* compass rail — what the tool has learned steers where it looks */}
      {learned.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-4 rounded-xl border border-border bg-muted/40 px-4 py-3.5">
          <div className="min-w-[150px] max-w-[210px]">
            <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Bússola da busca
            </div>
            <div className="mt-1 text-[12.5px] leading-snug text-muted-foreground">
              O que guia para onde olhar — aprendido entre buscas, não só desta.
            </div>
          </div>
          <div className="flex flex-1 flex-wrap gap-2">
            {learned.map((w) => {
              const pos = w.weight > 0;
              return (
                <span
                  key={w.feature}
                  title={`${w.label} ${pos ? "+" : "−"}${Math.abs(w.weight).toFixed(2)}`}
                  className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-[5px] text-[12px]"
                  style={{
                    color: pos ? "var(--cx-em)" : "var(--cx-rose)",
                    background: pos
                      ? "color-mix(in srgb, var(--cx-em-stroke) 11%, transparent)"
                      : "color-mix(in srgb, var(--cx-rose-stroke) 10%, transparent)",
                    borderColor: pos
                      ? "color-mix(in srgb, var(--cx-em-stroke) 38%, transparent)"
                      : "color-mix(in srgb, var(--cx-rose-stroke) 34%, transparent)",
                  }}
                >
                  <span>{w.label}</span>
                  <span className="font-mono text-[11px]" style={{ opacity: 0.85 }}>
                    {pos ? "↑" : "↓"}
                    {Math.abs(w.weight).toFixed(2).replace(".", ",")}
                  </span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* the map */}
      {arms.length > 0 && (
        <>
          <div className="overflow-x-auto overflow-y-hidden pb-2">
            <div className="relative" style={{ width: layout.W, height: layout.H }}>
              <svg
                width={layout.W}
                height={layout.H}
                className="pointer-events-none absolute inset-0 overflow-visible"
                aria-hidden="true"
              >
                {layout.edges.map((e) => (
                  <path
                    key={e.id}
                    d={e.d}
                    fill="none"
                    stroke={e.stroke}
                    strokeWidth={e.w}
                    strokeDasharray={e.dash}
                    strokeLinecap="round"
                    opacity={e.op}
                  />
                ))}
              </svg>

              {layout.labels.map((l) => (
                <div
                  key={l.t}
                  className="absolute top-1 font-mono text-[10px] uppercase tracking-[0.13em] text-muted-foreground"
                  style={{ left: l.left, width: NODE_W }}
                >
                  {l.t}
                </div>
              ))}

              {layout.nodes.map((n) => {
                const fc = fitFill(n.meanFit);
                const isSel = sel === n.key;
                return (
                  <button
                    key={n.key}
                    type="button"
                    onClick={() => setSel((cur) => (cur === n.key ? null : n.key))}
                    className="absolute flex flex-col justify-between gap-1 overflow-hidden rounded-xl text-left"
                    style={{
                      left: n.x,
                      top: n.y,
                      width: n.w,
                      height: n.h,
                      padding: "7px 10px",
                      background: fc.fill,
                      border: `1px solid ${fc.border}`,
                      opacity: n.pruned ? 0.5 : 1,
                      outline: isSel ? "2px solid var(--cx-brand)" : undefined,
                      outlineOffset: isSel ? 2 : undefined,
                      cursor: "pointer",
                    }}
                  >
                    <span
                      className="line-clamp-2 text-[12.5px] font-semibold leading-tight"
                      style={{ color: "var(--cx-fg)" }}
                    >
                      {n.title}
                    </span>
                    <span className="flex items-end justify-between gap-1.5">
                      <span className="flex items-baseline gap-1">
                        <span
                          className="font-mono text-[16px] font-medium leading-none"
                          style={{ color: "var(--cx-fg)" }}
                        >
                          {n.count}
                        </span>
                        <span className="text-[9px] uppercase tracking-[0.06em] text-muted-foreground">
                          cand.
                        </span>
                      </span>
                      <span
                        className="rounded-full border px-1.5 font-mono text-[11px] font-medium"
                        style={{
                          color: n.pruned ? "var(--cx-fg3)" : fc.text,
                          borderColor: n.pruned ? "var(--cx-border)" : fc.border,
                        }}
                      >
                        {Math.round(n.meanFit * 100)}%
                      </span>
                    </span>
                  </button>
                );
              })}

              {/* pool terminal */}
              <div
                className="absolute box-border flex flex-col gap-px rounded-[14px] bg-muted p-3.5"
                style={{
                  left: layout.pool.x,
                  top: layout.pool.y,
                  width: POOL_W,
                  height: POOL_H,
                  border: "1.5px solid var(--cx-brand)",
                }}
              >
                <div className="font-mono text-[9.5px] uppercase tracking-[0.13em] text-primary">
                  Pool final
                </div>
                <div
                  className="mt-0.5 font-mono text-[30px] font-medium leading-none"
                  style={{ color: "var(--cx-fg)" }}
                >
                  {view.candidatesExplored}
                </div>
                <div className="text-[11.5px] leading-tight text-muted-foreground">
                  avaliados · ordenados por FIT
                </div>
                <div className="mt-2 flex gap-0.5">
                  <span
                    className="h-1.5 flex-[3] rounded-full"
                    style={{ background: "var(--cx-em-stroke)" }}
                  />
                  <span
                    className="h-1.5 flex-[2] rounded-full"
                    style={{
                      background: "color-mix(in srgb, var(--cx-em-stroke) 45%, transparent)",
                    }}
                  />
                  <span
                    className="h-1.5 flex-[4] rounded-full"
                    style={{ background: "var(--cx-border)" }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* legend + inferred-lineage caveat */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11.5px] text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <span
                className="h-2.5 w-5 rounded-sm"
                style={{
                  background:
                    "linear-gradient(90deg, color-mix(in srgb, var(--cx-em-stroke) 12%, var(--cx-card)), var(--cx-em-stroke))",
                }}
              />
              cor = aproveitamento
            </span>
            <span className="inline-flex items-end gap-1.5">
              <span className="inline-flex items-end gap-0.5">
                <span className="h-2 w-1.5 rounded-sm bg-muted-foreground/50" />
                <span className="h-3.5 w-1.5 rounded-sm bg-muted-foreground/50" />
              </span>
              tamanho = candidatos
            </span>
            <span className="inline-flex items-center gap-1.5">
              <svg width="26" height="8" aria-hidden="true">
                <path d="M0 4 H26" stroke="var(--cx-em-stroke)" strokeWidth="2.5" />
              </svg>
              manteve / ramificou
            </span>
            <span className="inline-flex items-center gap-1.5">
              <svg width="26" height="8" aria-hidden="true">
                <path d="M0 4 H26" stroke="var(--cx-fg3)" strokeWidth="1.5" strokeDasharray="2 5" />
              </svg>
              {lineageReal ? "encerrado" : "descartado"}
            </span>
            <span className="italic">
              {lineageReal
                ? "ligações reais — registradas durante a busca (ramificação, nova categoria e encerramento)."
                : "as ligações entre rodadas são inferidas da diferença entre rodadas, não registradas."}
            </span>
          </div>

          {/* selected detail */}
          {selected ? (
            <div className="rounded-xl border border-border bg-card px-4 py-3">
              <div className="mb-2 flex flex-wrap items-center gap-2.5">
                <span className="text-sm font-semibold" style={{ color: "var(--cx-fg)" }}>
                  {selected.title}
                </span>
                <span
                  className="rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.08em]"
                  style={{
                    color: selected.pruned ? "var(--cx-fg3)" : fitFill(selected.meanFit).text,
                    borderColor: selected.pruned
                      ? "var(--cx-border)"
                      : fitFill(selected.meanFit).border,
                  }}
                >
                  {roleLabel(selected)}
                </span>
                <span className="font-mono text-xs text-muted-foreground">
                  {selected.count} candidatos · {Math.round(selected.meanFit * 100)}% aprov.
                </span>
              </div>
              <p className="mb-2.5 text-[12.5px] leading-relaxed text-muted-foreground">
                {whyText(selected)}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {selected.chips.map((chip) => (
                  <span
                    key={chip}
                    className="whitespace-nowrap rounded-full border border-input bg-card px-2 py-0.5 text-[11px] text-muted-foreground"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <p className="rounded-xl border border-dashed border-border px-3 py-2.5 text-xs italic text-muted-foreground">
              Clique em uma abordagem no mapa para ver seus filtros e por que avançou ou foi
              descartada.
            </p>
          )}
        </>
      )}

      <p
        className="border-t pt-3 text-[12.5px] leading-relaxed text-muted-foreground"
        style={{ borderTopColor: "var(--cx-border-soft)" }}
      >
        {budgetLine}
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Exploration map — FAITHFUL path (driven by the persisted decision log)
//
// When a run persisted a `decisionLog`, the map renders the TRUE per-batch frontier: not
// just the arms that were probed and surfaced candidates, but every arm the bandit
// CONSIDERED that batch — selected vs merely considered vs dormant vs revived — each sized
// by its novel-fit rate posterior, with the authoritative lineage (seed / carry / branch /
// nova categoria), the explore-vs-exploit meta decision per batch, empty probes shown, the
// recruiter's steering (on human-in-the-loop runs) distinct from the judge, and the honest
// termination reason. Legacy rows (no decisionLog) fall back to the inferred `ExplorationMap`.
// ─────────────────────────────────────────────────────────────────────────────

/** The panel's `--cx-*` color tokens (light + dark). Shared so both map paths match. */
const MAP_COLOR_TOKENS =
  "[--cx-card:#ffffff] [--cx-border:rgba(15,25,35,0.11)] [--cx-border-soft:rgba(15,25,35,0.06)] [--cx-fg:#0f1923] [--cx-fg3:rgba(15,25,35,0.45)] [--cx-brand:#f4364c] [--cx-em:#047857] [--cx-em-stroke:#059669] [--cx-rose:#be123c] [--cx-rose-stroke:#e11d48] [--cx-amber:#b45309] [--cx-violet:#7c3aed] [--cx-blue:#2563eb] dark:[--cx-card:#15191f] dark:[--cx-border:rgba(255,255,255,0.13)] dark:[--cx-border-soft:rgba(255,255,255,0.07)] dark:[--cx-fg:#f3f4f6] dark:[--cx-fg3:rgba(243,244,246,0.45)] dark:[--cx-em:#34d399] dark:[--cx-em-stroke:#34d399] dark:[--cx-rose:#fb7185] dark:[--cx-rose-stroke:#fb7185] dark:[--cx-amber:#fbbf24] dark:[--cx-violet:#a78bfa] dark:[--cx-blue:#60a5fa]";

/** The learned cross-run posterior compass — what steers WHERE the tool looks. */
function CompassRail({ learned }: { learned: PosteriorWeight[] }) {
  if (learned.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-4 rounded-xl border border-border bg-muted/40 px-4 py-3.5">
      <div className="min-w-[150px] max-w-[210px]">
        <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Bússola da busca
        </div>
        <div className="mt-1 text-[12.5px] leading-snug text-muted-foreground">
          O que guia para onde olhar — aprendido entre buscas, não só desta.
        </div>
      </div>
      <div className="flex flex-1 flex-wrap gap-2">
        {learned.map((w) => {
          const pos = w.weight > 0;
          return (
            <span
              key={w.feature}
              title={`${w.label} ${pos ? "+" : "−"}${Math.abs(w.weight).toFixed(2)}`}
              className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-[5px] text-[12px]"
              style={{
                color: pos ? "var(--cx-em)" : "var(--cx-rose)",
                background: pos
                  ? "color-mix(in srgb, var(--cx-em-stroke) 11%, transparent)"
                  : "color-mix(in srgb, var(--cx-rose-stroke) 10%, transparent)",
                borderColor: pos
                  ? "color-mix(in srgb, var(--cx-em-stroke) 38%, transparent)"
                  : "color-mix(in srgb, var(--cx-rose-stroke) 34%, transparent)",
              }}
            >
              <span>{w.label}</span>
              <span className="font-mono text-[11px]" style={{ opacity: 0.85 }}>
                {pos ? "↑" : "↓"}
                {Math.abs(w.weight).toFixed(2).replace(".", ",")}
              </span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

/** How an arm sat in a batch — the visual state distinguished on the map. */
type FrontierNodeState = "selected" | "considered" | "dormant" | "revived";

/** One arm as considered in one batch (a node in the faithful map). */
interface FrontierNode {
  key: string; // `${batchIndex}:${armKey}`
  armKey: string;
  batchIndex: number;
  title: string;
  chips: string[];
  origin: ArmOrigin;
  parentKey?: string;
  state: FrontierNodeState;
  /** Was this arm Thompson-SELECTED (probed a page) this batch? This is the pulled-vs-
   *  considered split the map draws: pulled arms are full nodes; unpulled (considered)
   *  arms collapse into a per-column box. NB dormancy is only ever set on a probed arm,
   *  so `pulled === false` is exactly the live-but-not-selected set (no dormancy hidden). */
  pulled: boolean;
  /** Novel-fit rate posterior at decision time (mean + 80% credible interval). */
  rateMean: number;
  rateLo: number;
  rateHi: number;
  pulls: number;
  draw?: number;
  /** Novel FIT-passing this pull added (probed arms only). `emptyProbe` = probed, 0 novel. */
  novelFit?: number;
  emptyProbe: boolean;
  /** Human-in-the-loop: the blended human+judge steering signal (distinct from the judge). */
  humanBlendedNovelFit?: number;
  dormantReason?: "lowYield" | "smallPage" | "depthCap" | null;
  /** Joined from `rounds` by armKey: the candidates this arm surfaced this batch + their FIT. */
  candidateCount: number;
  meanFit: number;
}

interface BatchColumn {
  index: number;
  meta: BatchDecision["meta"];
  nodes: FrontierNode[];
}

interface DecisionMapData {
  columns: BatchColumn[];
  terminationReason: ExplorationTerminationReason | null;
  hasHuman: boolean;
}

/** Build the faithful map data from the persisted decision log, joining each arm to the
 *  candidates it surfaced (in `rounds`) by `armKey`. Exported for unit testing. */
export function buildDecisionMap(
  log: ExplorationDecisionLog,
  rounds: ExplorationView["rounds"],
): DecisionMapData {
  const roundByIndex = new Map(rounds.map((r) => [r.index, r] as const));
  const statsFor = (batchIndex: number, armKey: string): { count: number; meanFit: number } => {
    const arm = roundByIndex.get(batchIndex)?.arms.find((a) => a.armKey === armKey);
    if (!arm) return { count: 0, meanFit: 0 };
    const ids = new Set<string>();
    let sum = 0;
    for (const c of arm.candidates) {
      if (!c.judgement || ids.has(c.id)) continue;
      ids.add(c.id);
      sum += c.judgement.score;
    }
    return { count: ids.size, meanFit: ids.size ? sum / ids.size : 0 };
  };

  let hasHuman = false;
  const columns: BatchColumn[] = log.batches.map((b) => ({
    index: b.index,
    meta: b.meta,
    nodes: b.arms.map((a) => {
      const lineage = log.arms[a.armKey];
      const stats = statsFor(b.index, a.armKey);
      if (a.humanBlendedNovelFit !== undefined) hasHuman = true;
      const state: FrontierNodeState = a.revived
        ? "revived"
        : a.dormant
          ? "dormant"
          : a.selected
            ? "selected"
            : "considered";
      return {
        key: `${b.index}:${a.armKey}`,
        armKey: a.armKey,
        batchIndex: b.index,
        title: lineage?.facetChips[0] ?? "Sem título",
        chips: lineage?.facetChips ?? [],
        origin: lineage?.origin ?? "seed",
        parentKey: lineage?.parentKey,
        state,
        pulled: a.selected,
        rateMean: a.beta > 0 ? a.alpha / a.beta : 0,
        rateLo: a.rateLo,
        rateHi: a.rateHi,
        pulls: a.pulls,
        draw: a.draw,
        novelFit: a.novelFit,
        emptyProbe: a.selected && (a.novelFit ?? 0) === 0,
        humanBlendedNovelFit: a.humanBlendedNovelFit,
        dormantReason: a.dormantReason,
        candidateCount: stats.count,
        meanFit: stats.meanFit,
      };
    }),
  }));
  return { columns, terminationReason: log.terminationReason, hasHuman };
}

const RATE_FULL = 6; // a novel-fit rate at/above this reads as "rich" (sizing ceiling)
const decisionNodeHeight = (rateMean: number) => 58 + clamp(rateMean / RATE_FULL, 0, 1) * 44;

/** What pulling an arm THIS round means in bandit terms — the map's color axis (NOT FIT).
 *  It's the arm's birth origin on its FIRST pull, but a RE-pull of an arm already probed in
 *  an earlier round is a `carry` (exploitation of a known-good arm) regardless of how it was
 *  born — so a seed re-sondado in a later round shows as `carry`, not `seed`. */
type NodeRole = ArmOrigin | "carry";

const ROLE_TOKEN: Record<NodeRole, string> = {
  seed: "var(--cx-em-stroke)", // initial action space — exploration
  explore: "var(--cx-violet)", // LLM-injected NEW category — exploration
  branch: "var(--cx-amber)", // refined child of a depleting vein — exploitation
  carry: "var(--cx-blue)", // re-pull of a still-producing arm — exploitation
};

const DECISION_ROLE_LABEL: Record<NodeRole, string> = {
  seed: "semente",
  branch: "ramificação",
  explore: "nova categoria",
  carry: "re-sondado",
};

/** Fill/border/opacity for a node, colored by its ROLE (seed/branch/explore/carry). FIT
 *  still shows on the node badge + detail; node SIZE still encodes the λ posterior. A
 *  dormant arm (pulled, then confidently exhausted) keeps its hue but dims + goes dashed. */
function decisionNodeStyle(
  role: NodeRole,
  dormant: boolean,
): {
  fill: string;
  border: string;
  text: string;
  dashed: boolean;
  opacity: number;
} {
  const token = ROLE_TOKEN[role];
  return {
    fill: `color-mix(in srgb, ${token} ${dormant ? 10 : 18}%, var(--cx-card))`,
    border: `color-mix(in srgb, ${token} ${dormant ? 30 : 52}%, transparent)`,
    text: token,
    dashed: dormant,
    opacity: dormant ? 0.6 : 1,
  };
}

interface DNode extends FrontierNode {
  position: number;
  /** Explore/exploit role for THIS pull (carry = re-pull of an already-probed arm). */
  role: NodeRole;
  x: number;
  y: number;
  w: number;
  h: number;
  cx0: number;
  cx1: number;
  cy: number;
}
/** The collapsed "considered" box for one column — the arms that were live in the
 *  frontier but NOT selected this batch, folded behind a click so the column stays legible. */
interface DBox {
  position: number;
  x: number;
  y: number;
  w: number;
  h: number;
  arms: FrontierNode[];
}
interface DLayout {
  W: number;
  H: number;
  nodes: DNode[];
  edges: MapEdge[];
  columns: { label: string; meta: BatchDecision["meta"]; left: number }[];
  boxes: DBox[];
  pool: { x: number; y: number; cx0: number; cy: number };
}

/** Lay the batches out as left→right columns of the full frontier, feeding the pool.
 *  Real lineage: a node's parent is the most-recent prior-column node with the same
 *  `armKey` (carried) or its `parentKey` (branched) — authoritative, never inferred. */
const BOX_H = 46; // the collapsed "considered" box height

function layoutDecisionMap(data: DecisionMapData): DLayout {
  const cols = data.columns;
  const nCols = cols.length;
  // Split each column: PULLED arms (Thompson-selected — incl. those that went dormant
  // after their pull) get full positioned nodes + lineage edges; UNPULLED (considered)
  // arms collapse into ONE box per column, kept off the canvas until expanded.
  const pulledOf = (c: BatchColumn) =>
    c.nodes.filter((n) => n.pulled).sort((a, b) => b.rateMean - a.rateMean);
  const unpulledOf = (c: BatchColumn) =>
    c.nodes.filter((n) => !n.pulled).sort((a, b) => b.rateMean - a.rateMean);

  const colH = cols.map((c) => {
    const pulled = pulledOf(c);
    const stack =
      pulled.reduce((s, n) => s + decisionNodeHeight(n.rateMean), 0) +
      GAP_Y * Math.max(0, pulled.length - 1);
    return stack + (unpulledOf(c).length > 0 ? BOX_H + GAP_Y : 0);
  });
  const maxH = Math.max(...colH, POOL_H, 1);
  const X = (i: number) => PAD_L + i * (NODE_W + GAP_X);
  const nodes: DNode[] = [];
  const boxes: DBox[] = [];
  cols.forEach((c, ci) => {
    let y = TOP_PAD + (maxH - (colH[ci] ?? 0)) / 2;
    for (const a of pulledOf(c)) {
      const h = decisionNodeHeight(a.rateMean);
      // A re-pull of an arm already pulled in an EARLIER column is a CARRY (exploitation);
      // a first pull keeps the arm's birth origin. (Same-column armKeys are unique, so any
      // prior match is from an earlier round.)
      const role: NodeRole = nodes.some((m) => m.armKey === a.armKey) ? "carry" : a.origin;
      nodes.push({
        ...a,
        role,
        position: ci,
        x: X(ci),
        y,
        w: NODE_W,
        h,
        cx0: X(ci),
        cx1: X(ci) + NODE_W,
        cy: y + h / 2,
      });
      y += h + GAP_Y;
    }
    const unpulled = unpulledOf(c);
    if (unpulled.length > 0)
      boxes.push({ position: ci, x: X(ci), y, w: NODE_W, h: BOX_H, arms: unpulled });
  });

  const priorByArmKey = (armKey: string, beforePos: number): DNode | null => {
    let best: DNode | null = null;
    for (const n of nodes) {
      if (n.armKey !== armKey || n.position >= beforePos) continue;
      if (!best || n.position > best.position) best = n;
    }
    return best;
  };

  const poolX = X(nCols);
  const poolY = TOP_PAD + (maxH - POOL_H) / 2;
  const pool = { x: poolX, y: poolY, cx0: poolX, cy: poolY + POOL_H / 2 };

  const edges: MapEdge[] = [];
  for (const n of nodes) {
    // Carry (same arm probed/considered again) takes precedence over the branch edge, so a
    // branch child links to its parent only on its FIRST appearance.
    const parent =
      priorByArmKey(n.armKey, n.position) ?? priorByArmKey(n.parentKey ?? "", n.position);
    if (!parent) continue;
    // Color the lineage edge by the CHILD's origin, so edges match the nodes; a dormant
    // child's edge dims + dashes (its vein ended here).
    const live = n.state !== "dormant";
    edges.push({
      id: `e${n.key}`,
      d: bezier(parent.cx1, parent.cy, n.cx0, n.cy),
      stroke: ROLE_TOKEN[n.role],
      w: live ? edgeWidth(n.candidateCount) : 1.5,
      dash: live ? "0" : "3 5",
      op: live ? 0.85 : 0.4,
    });
  }
  for (const n of nodes) {
    if (n.position !== nCols - 1) continue;
    if (n.state !== "selected" && n.state !== "revived") continue;
    const strong = n.meanFit >= 0.2;
    edges.push({
      id: `p${n.key}`,
      d: bezier(n.cx1, n.cy, pool.cx0, pool.cy),
      stroke: strong ? "var(--cx-em-stroke)" : "var(--cx-amber)",
      w: strong ? 4 : 2,
      dash: strong ? "0" : "5 5",
      op: strong ? 0.95 : 0.7,
    });
  }
  for (const n of nodes) {
    if (n.state !== "dormant") continue;
    if (nodes.some((m) => m.armKey === n.armKey && m.position > n.position)) continue;
    edges.push({
      id: `x${n.key}`,
      d: `M ${n.cx1} ${n.cy} c 26 2 40 18 58 32`,
      stroke: "var(--cx-fg3)",
      w: 1.5,
      dash: "2 5",
      op: 0.5,
    });
  }

  const columns = cols.map((c, i) => ({ label: `Rodada ${i + 1}`, meta: c.meta, left: X(i) }));
  return { W: poolX + POOL_W + PAD_L, H: maxH + TOP_PAD + 26, nodes, edges, columns, boxes, pool };
}

const TERMINATION_CAPTION: Record<ExplorationTerminationReason, string> = {
  target: "atingiu a meta de candidatos com FIT suficiente.",
  budget: "usou todo o orçamento de páginas.",
  earlyStop: "parou cedo — o rendimento novo caiu por lotes seguidos (busca fácil).",
  frontierExhausted: "encerrou — a fronteira de abordagens se esgotou (nada vivo a sondar).",
  batchError: "encerrou após uma falha de lote — finalizou com o que já havia reunido.",
};

const DECISION_STATE_LABEL: Record<FrontierNodeState, string> = {
  selected: "sondado",
  considered: "considerado",
  dormant: "encerrado",
  revived: "reativado",
};

const DECISION_ORIGIN_LABEL: Record<ArmOrigin, string> = {
  seed: "semente",
  branch: "ramificação",
  explore: "nova categoria",
};

const DECISION_DORMANCY_WHY: Record<string, string> = {
  lowYield: "rendimento novo confiantemente baixo — encerrada (reversível se sobrar orçamento).",
  smallPage: "a página retornou poucos perfis — oferta esgotada (terminal).",
  depthCap: "atingiu o limite de profundidade do arm (terminal).",
};

function decisionWhyText(n: FrontierNode, role: NodeRole): string {
  if (n.state === "dormant")
    return `Encerrada: ${DECISION_DORMANCY_WHY[n.dormantReason ?? ""] ?? "sem rendimento novo."}`;
  if (n.state === "considered")
    return "Estava viva na fronteira mas não foi sorteada (Thompson) neste lote — considerada, não sondada.";
  if (n.state === "revived")
    return "Reativada de uma dormência de baixo rendimento para não deixar orçamento ocioso.";
  if (role === "carry")
    return "Re-sondada — o mesmo arm seguiu rendendo, então foi sorteado de novo (aproveitamento do que já funciona).";
  if (n.origin === "explore")
    return "Categoria NOVA injetada pela exploração dirigida (LLM) — fora do conjunto inicial de sementes.";
  if (n.origin === "branch")
    return "Ramificou de um arm rico que começava a esgotar — exploração local do mesmo veio.";
  return "Semente inicial da fronteira (conjunto de ações da primeira rodada).";
}

/**
 * The FAITHFUL exploration map — driven by the persisted decision log. Compass rail
 * (learned posterior), then the batches as columns of the FULL frontier: selected /
 * considered / dormant / revived nodes sized by their rate posterior, with the meta
 * explore-vs-exploit decision per column, real lineage edges, empty probes, and an honest
 * termination footer. Clicking a node reveals its posterior, selection, and lineage.
 */
function DecisionMap({
  view,
  learned,
  running,
}: {
  view: ExplorationView;
  learned: PosteriorWeight[];
  running: boolean;
}) {
  const log = view.decisionLog as ExplorationDecisionLog;
  const [sel, setSel] = useState<string | null>(null);
  const [openBox, setOpenBox] = useState<number | null>(null);
  const data = useMemo(() => buildDecisionMap(log, view.rounds), [log, view.rounds]);
  const layout = useMemo(() => layoutDecisionMap(data), [data]);
  const selected = sel ? (layout.nodes.find((n) => n.key === sel) ?? null) : null;
  const openBoxData =
    openBox !== null ? (layout.boxes.find((b) => b.position === openBox) ?? null) : null;

  const spent = `${view.pagesUsed} de ${view.budgetPages} páginas`;
  const budgetLine = running
    ? `Em andamento · ${spent} usadas até agora.`
    : data.terminationReason
      ? `Custo · ${spent} — ${TERMINATION_CAPTION[data.terminationReason]}`
      : `Custo · ${spent} usadas.`;

  return (
    <div className={cn("flex flex-col gap-4 border-t border-border px-4 py-4", MAP_COLOR_TOKENS)}>
      <CompassRail learned={learned} />

      {layout.nodes.length > 0 && (
        <>
          <div className="overflow-x-auto overflow-y-hidden pb-2">
            <div className="relative" style={{ width: layout.W, height: layout.H }}>
              <svg
                width={layout.W}
                height={layout.H}
                className="pointer-events-none absolute inset-0 overflow-visible"
                aria-hidden="true"
              >
                {layout.edges.map((e) => (
                  <path
                    key={e.id}
                    d={e.d}
                    fill="none"
                    stroke={e.stroke}
                    strokeWidth={e.w}
                    strokeDasharray={e.dash}
                    strokeLinecap="round"
                    opacity={e.op}
                  />
                ))}
              </svg>

              {layout.columns.map((c) => (
                <div
                  key={c.label}
                  className="absolute top-0 flex flex-col gap-0.5"
                  style={{ left: c.left, width: NODE_W }}
                >
                  <span className="font-mono text-[10px] uppercase tracking-[0.13em] text-muted-foreground">
                    {c.label}
                  </span>
                  <span className="inline-flex flex-wrap items-center gap-1">
                    <span
                      className="rounded-full border px-1.5 py-px text-[9.5px] font-semibold uppercase tracking-[0.06em]"
                      style={{
                        color: c.meta.decision === "explore" ? "var(--cx-brand)" : "var(--cx-em)",
                        borderColor:
                          c.meta.decision === "explore"
                            ? "color-mix(in srgb, var(--cx-brand) 40%, transparent)"
                            : "color-mix(in srgb, var(--cx-em-stroke) 40%, transparent)",
                      }}
                    >
                      {c.meta.decision === "explore" ? "explorar" : "aproveitar"}
                    </span>
                    {c.meta.horizonGuard && (
                      <span className="text-[9px] uppercase tracking-[0.06em] text-muted-foreground">
                        horizonte
                      </span>
                    )}
                    {c.meta.exploreOutcome && c.meta.exploreOutcome !== "proposed" && (
                      <span className="text-[9px] uppercase tracking-[0.06em] text-muted-foreground">
                        {c.meta.exploreOutcome === "saturated" ? "sem novidade" : "falhou"}
                      </span>
                    )}
                  </span>
                </div>
              ))}

              {layout.nodes.map((n) => {
                const st = decisionNodeStyle(n.role, n.state === "dormant");
                const isSel = sel === n.key;
                return (
                  <button
                    key={n.key}
                    type="button"
                    onClick={() => {
                      setSel((cur) => (cur === n.key ? null : n.key));
                      setOpenBox(null);
                    }}
                    className="absolute flex flex-col justify-between gap-1 overflow-hidden rounded-xl text-left"
                    style={{
                      left: n.x,
                      top: n.y,
                      width: n.w,
                      height: n.h,
                      padding: "7px 10px",
                      background: st.fill,
                      border: `${st.dashed ? "1px dashed" : "1px solid"} ${st.border}`,
                      opacity: st.opacity,
                      outline: isSel
                        ? "2px solid var(--cx-brand)"
                        : n.state === "revived"
                          ? "2px dotted var(--cx-rose-stroke)"
                          : undefined,
                      outlineOffset: isSel || n.state === "revived" ? 2 : undefined,
                      cursor: "pointer",
                    }}
                  >
                    <span
                      className="line-clamp-2 text-[12.5px] font-semibold leading-tight"
                      style={{ color: "var(--cx-fg)" }}
                    >
                      {n.title}
                    </span>
                    <span className="flex items-end justify-between gap-1.5">
                      <span className="flex items-baseline gap-1">
                        <span
                          className="font-mono text-[13px] font-medium leading-none"
                          style={{ color: "var(--cx-fg)" }}
                          title="rendimento novo por página (posterior)"
                        >
                          λ{n.rateMean.toFixed(1).replace(".", ",")}
                        </span>
                        {n.state === "selected" || n.state === "revived" ? (
                          <span className="text-[9px] uppercase tracking-[0.06em] text-muted-foreground">
                            {n.emptyProbe ? "0 novos" : `${n.candidateCount} cand.`}
                          </span>
                        ) : (
                          <span className="text-[9px] uppercase tracking-[0.06em] text-muted-foreground">
                            {DECISION_STATE_LABEL[n.state]}
                          </span>
                        )}
                      </span>
                      {(n.state === "selected" || n.state === "revived") && (
                        <span
                          className="rounded-full border px-1.5 font-mono text-[11px] font-medium"
                          style={{ color: st.text, borderColor: st.border }}
                        >
                          {Math.round(n.meanFit * 100)}%
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}

              {/* collapsed "considered" box per column (unpulled arms) */}
              {layout.boxes.map((box) => {
                const isOpen = openBox === box.position;
                return (
                  <button
                    key={`box-${box.position}`}
                    type="button"
                    onClick={() => {
                      setOpenBox((cur) => (cur === box.position ? null : box.position));
                      setSel(null);
                    }}
                    className="absolute flex items-center justify-center gap-1.5 rounded-xl border border-dashed"
                    style={{
                      left: box.x,
                      top: box.y,
                      width: box.w,
                      height: box.h,
                      borderColor: "var(--cx-border)",
                      background: "color-mix(in srgb, var(--cx-fg3) 4%, var(--cx-card))",
                      cursor: "pointer",
                      outline: isOpen ? "2px solid var(--cx-brand)" : undefined,
                      outlineOffset: isOpen ? 2 : undefined,
                    }}
                    title="abordagens vivas mas não sondadas neste lote"
                  >
                    <span
                      className="font-mono text-[13px] font-medium"
                      style={{ color: "var(--cx-fg)" }}
                    >
                      +{box.arms.length}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
                      {box.arms.length === 1 ? "considerada" : "consideradas"}
                    </span>
                    <ChevronDown
                      size={13}
                      className={cn(
                        "text-muted-foreground transition-transform",
                        isOpen && "rotate-180",
                      )}
                    />
                  </button>
                );
              })}

              {/* pool terminal */}
              <div
                className="absolute box-border flex flex-col gap-px rounded-[14px] bg-muted p-3.5"
                style={{
                  left: layout.pool.x,
                  top: layout.pool.y,
                  width: POOL_W,
                  height: POOL_H,
                  border: "1.5px solid var(--cx-brand)",
                }}
              >
                <div className="font-mono text-[9.5px] uppercase tracking-[0.13em] text-primary">
                  Pool final
                </div>
                <div
                  className="mt-0.5 font-mono text-[30px] font-medium leading-none"
                  style={{ color: "var(--cx-fg)" }}
                >
                  {view.candidatesExplored}
                </div>
                <div className="text-[11.5px] leading-tight text-muted-foreground">
                  avaliados · ordenados por FIT
                </div>
              </div>
            </div>
          </div>

          {/* legend */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11.5px] text-muted-foreground">
            <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground/70">
              cor = mecanismo:
            </span>
            {(["seed", "branch", "explore", "carry"] as const).map((role) => (
              <span key={role} className="inline-flex items-center gap-1.5">
                <span
                  className="h-3 w-3 rounded-sm"
                  style={{
                    background: `color-mix(in srgb, ${ROLE_TOKEN[role]} 55%, var(--cx-card))`,
                    border: `1px solid color-mix(in srgb, ${ROLE_TOKEN[role]} 60%, transparent)`,
                  }}
                />
                {DECISION_ROLE_LABEL[role]}
              </span>
            ))}
            <span className="inline-flex items-center gap-1.5">
              <span
                className="rounded border border-dashed px-1 font-mono text-[9px]"
                style={{ borderColor: "var(--cx-border)" }}
              >
                +N
              </span>
              consideradas — não sorteadas (clique p/ abrir)
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-3.5 w-4 rounded-sm border border-dashed border-muted-foreground/40 opacity-50" />
              encerrado (tracejado)
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span
                className="h-3.5 w-4 rounded-sm"
                style={{ outline: "2px dotted var(--cx-rose-stroke)", outlineOffset: -1 }}
              />
              reativado
            </span>
            <span className="inline-flex items-center gap-1.5">
              tamanho = λ (posterior de rendimento)
            </span>
            <span className="italic">
              ligações e estados reais — registrados durante a busca, não inferidos.
            </span>
          </div>

          {/* selected detail */}
          {selected ? (
            <div className="rounded-xl border border-border bg-card px-4 py-3">
              <div className="mb-2 flex flex-wrap items-center gap-2.5">
                <span className="text-sm font-semibold" style={{ color: "var(--cx-fg)" }}>
                  {selected.title}
                </span>
                <span className="rounded-full border border-input px-2 py-0.5 text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                  {DECISION_STATE_LABEL[selected.state]} ·{" "}
                  {selected.role === "carry"
                    ? `re-sondado (${DECISION_ORIGIN_LABEL[selected.origin]})`
                    : DECISION_ROLE_LABEL[selected.role]}
                </span>
                <span className="font-mono text-xs text-muted-foreground">
                  λ {selected.rateMean.toFixed(2).replace(".", ",")} [
                  {selected.rateLo.toFixed(1).replace(".", ",")}–
                  {selected.rateHi.toFixed(1).replace(".", ",")}] · {selected.pulls}{" "}
                  {selected.pulls === 1 ? "sondagem" : "sondagens"}
                </span>
              </div>
              <p className="mb-2 text-[12.5px] leading-relaxed text-muted-foreground">
                {decisionWhyText(selected, selected.role)}
              </p>
              {(selected.state === "selected" || selected.state === "revived") && (
                <div className="mb-2.5 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11.5px] text-muted-foreground">
                  <span>{selected.candidateCount} candidatos surgidos</span>
                  <span>{selected.novelFit ?? 0} novos com FIT</span>
                  <span>{Math.round(selected.meanFit * 100)}% FIT médio</span>
                  {selected.draw !== undefined && (
                    <span>sorteio {selected.draw.toFixed(2).replace(".", ",")}</span>
                  )}
                  {selected.humanBlendedNovelFit !== undefined && (
                    <span style={{ color: "var(--cx-brand)" }}>
                      recrutador (misto){" "}
                      {selected.humanBlendedNovelFit.toFixed(1).replace(".", ",")}
                    </span>
                  )}
                </div>
              )}
              <div className="flex flex-wrap gap-1.5">
                {selected.chips.map((chip) => (
                  <span
                    key={chip}
                    className="whitespace-nowrap rounded-full border border-input bg-card px-2 py-0.5 text-[11px] text-muted-foreground"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>
          ) : openBoxData ? (
            <div className="rounded-xl border border-border bg-card px-4 py-3">
              <div className="mb-1.5 text-sm font-semibold" style={{ color: "var(--cx-fg)" }}>
                {openBoxData.arms.length} abordagens consideradas · Rodada{" "}
                {openBoxData.position + 1}
              </div>
              <p className="mb-2.5 text-[12.5px] leading-relaxed text-muted-foreground">
                Vivas na fronteira mas não sorteadas (Thompson) neste lote — o loop preferiu as
                sondadas. Voltam a concorrer nos lotes seguintes.
              </p>
              <div className="flex flex-col gap-1.5">
                {openBoxData.arms.map((a) => (
                  <div
                    key={a.key}
                    className="flex items-center justify-between gap-3 rounded-lg border border-input px-2.5 py-1.5"
                  >
                    <span className="truncate text-[12.5px]" style={{ color: "var(--cx-fg)" }}>
                      {a.title}
                    </span>
                    <span className="flex shrink-0 items-center gap-2.5 font-mono text-[11px] text-muted-foreground">
                      <span className="uppercase tracking-[0.06em]">
                        {DECISION_ORIGIN_LABEL[a.origin]}
                      </span>
                      <span title="rendimento novo por página (posterior)">
                        λ{a.rateMean.toFixed(1).replace(".", ",")}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="rounded-xl border border-dashed border-border px-3 py-2.5 text-xs italic text-muted-foreground">
              Clique em uma abordagem no mapa para ver seu posterior, se foi sondada ou apenas
              considerada, e sua linhagem.
              {data.hasHuman &&
                " As avaliações do recrutador aparecem no detalhe de cada arm sondado."}
            </p>
          )}
        </>
      )}

      <p
        className="border-t pt-3 text-[12.5px] leading-relaxed text-muted-foreground"
        style={{ borderTopColor: "var(--cx-border-soft)" }}
      >
        {budgetLine}
      </p>
    </div>
  );
}

/**
 * The original brief, collapsed by default. Sits above "Como exploramos" so the
 * recruiter can reread the exact prompt that produced this run — useful on reopened
 * or shared explorations where the input box is long gone. Read-only receipt.
 *
 * When the briefing scoped the run to a Level-Assessment model (and optionally a
 * discipline + target level), those choices ride along as chips — they're as much
 * part of "what produced this run" as the prompt text, and equally invisible once
 * the picker is gone.
 */
function BriefPanel({ brief, laScope }: { brief: string; laScope?: ExplorationView["laScope"] }) {
  const [open, setOpen] = useState(false);
  const text = brief.trim();
  if (!text) return null;

  // Model → discipline → level, in scope order; each optional after the model.
  const scopeChips = laScope
    ? ([laScope.modelName, laScope.disciplineLabel, laScope.levelLabel].filter(Boolean) as string[])
    : [];

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="inline-flex min-w-0 items-center gap-2.5">
          <FileText size={14} className="shrink-0 text-muted-foreground" aria-hidden />
          <span className="truncate text-[13px] text-muted-foreground">
            {open ? "Briefing original" : text}
          </span>
        </span>
        <span className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-primary">
          {open ? "Ocultar" : "Ver briefing"}
          <ChevronDown size={15} className={cn("transition-transform", open && "rotate-180")} />
        </span>
      </button>

      {open && (
        <div className="border-t px-4 py-3" style={{ borderTopColor: "var(--cx-border-soft)" }}>
          {scopeChips.length > 0 && (
            <div className="mb-3 flex flex-wrap items-center gap-1.5">
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                <Layers size={12} className="opacity-70" aria-hidden />
                Modelo de nível:
              </span>
              {scopeChips.map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center rounded-md bg-primary/10 px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-primary"
                >
                  {c}
                </span>
              ))}
            </div>
          )}
          <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-foreground">{text}</p>
        </div>
      )}
    </div>
  );
}

/**
 * "Como exploramos" — the autonomous run's transparency panel. Collapsed, it's a
 * one-line receipt (status · rounds · profiles judged · approaches tried). Expanded,
 * it renders the exploration MAP (`ExplorationMap`): the search told as a journey —
 * a compass of what the tool learned, then the rounds as columns of arm nodes that
 * branch, carry, or get dropped, feeding the final pool.
 */
function ExplorationStrip({ view, running }: { view: ExplorationView; running: boolean }) {
  const [open, setOpen] = useState(false);
  const learned = [...view.posterior]
    .filter((w) => Math.abs(w.weight) > 0.05)
    .sort((a, b) => Math.abs(b.weight) - Math.abs(a.weight))
    .slice(0, 8);
  const approachCount = useMemo(() => topApproaches(view.rounds).length, [view.rounds]);
  // Prefer the faithful map when the run persisted a decision log; legacy rows fall back.
  const decisionLog =
    view.decisionLog?.version === 1 && view.decisionLog.batches.length > 0
      ? view.decisionLog
      : null;
  const exploreBatches = decisionLog
    ? decisionLog.batches.filter((b) => b.meta.decision === "explore").length
    : 0;
  if (learned.length === 0 && approachCount === 0 && !decisionLog) return null;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="inline-flex min-w-0 items-center gap-2.5">
          <span
            className={cn(
              "h-2 w-2 shrink-0 rounded-full",
              running ? "animate-pulse bg-amber-500" : "bg-emerald-500",
            )}
            aria-hidden
          />
          <span className="truncate text-[13px] text-muted-foreground">
            {running ? "Explorando" : "Concluído"} · {view.rounds.length}{" "}
            {view.rounds.length === 1 ? "rodada" : "rodadas"} · {view.candidatesExplored} avaliados
            {approachCount > 0 && ` · ${approachCount} abordagens`}
            {exploreBatches > 0 &&
              ` · ${exploreBatches} ${exploreBatches === 1 ? "exploração" : "explorações"}`}
          </span>
        </span>
        <span className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-primary">
          {open ? "Ocultar" : "Como exploramos"}
          <ChevronDown size={15} className={cn("transition-transform", open && "rotate-180")} />
        </span>
      </button>

      {open &&
        (decisionLog ? (
          <DecisionMap view={view} learned={learned} running={running} />
        ) : (
          <ExplorationMap view={view} learned={learned} running={running} />
        ))}
    </div>
  );
}

/**
 * A judged candidate. The judge's fit verdict is the card's single headline number
 * (Explore is ranked by the judge, not the composite); its reasoning renders inside
 * the card, and the composite is demoted to the "detalhamento do score" breakdown.
 */
function ResultCard({
  candidate,
  rank,
  selected,
  selectionDisabled,
  onToggleSelect,
}: {
  candidate: JudgedCandidate;
  rank: number;
  selected: boolean;
  selectionDisabled: boolean;
  onToggleSelect: () => void;
}) {
  // The ranked list only ever carries judged candidates, but the type is optional.
  if (!candidate.judgement) return null;
  const human = candidate.judgement.humanRating;
  return (
    <CandidateCard
      candidate={candidate}
      fit={candidate.judgement}
      rank={rank}
      selectable
      selected={selected}
      selectionDisabled={selectionDisabled}
      onToggleSelect={onToggleSelect}
      actions={human ? <HumanVerdictBadge rating={human} /> : undefined}
    />
  );
}

/** Read-only chip on a result card showing the recruiter's own verdict from a
 *  human-in-the-loop run (present only when this candidate was sampled + rated). */
function HumanVerdictBadge({ rating }: { rating: TernaryRating }) {
  const map = {
    up: { icon: ThumbsUp, label: "você: bom", cls: "border-emerald-500/40 text-emerald-600" },
    mid: { icon: Minus, label: "você: indiferente", cls: "border-amber-500/40 text-amber-600" },
    down: { icon: ThumbsDown, label: "você: ruim", cls: "border-rose-500/40 text-rose-600" },
  }[rating];
  const Icon = map.icon;
  return (
    <span
      className={cn(
        "flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium",
        map.cls,
      )}
    >
      <Icon size={12} /> {map.label}
    </span>
  );
}

/** A recruiter's decision on a fed candidate: a ternary verdict, or `skip` (no opinion —
 *  defers to the auto-judge in the blend, distinct from 😐/mid which is a real reward). */
type FeedDecision = TernaryRating | "skip";

/** One item in the review queue: a judged candidate + the arm chips that surfaced it. */
interface FeedItem {
  candidate: JudgedCandidate;
  chips: string[];
  roundIndex: number;
}

/**
 * The flowing human-in-the-loop review feed. The run streams candidates in a bounded
 * look-ahead buffer (`pending`, grouped by round); the recruiter reacts to ONE at a time
 * (👍/😐/👎 or skip — keyboard-driven, auto-advance), and each completed round's ratings
 * submit AUTOMATICALLY to steer the search — no "Continuar" button, no wall of cards.
 * Candidates keep arriving because the search probes the next round while these are rated.
 *
 * Submits are per round (the fold unit) and in order: a round submits once every candidate
 * in it is decided AND no earlier submitted round is still in the buffer (its fold in
 * flight). That gate — "a submitted round still buffered ⇒ wait" — is robust to poll
 * timing without a manual lock, since a folded round only leaves the buffer once the run
 * re-parks (`awaiting_input`).
 */
function ReviewFeed({
  view,
  pending,
  awaiting,
  onSubmitRound,
  onFinalize,
}: {
  view: ExplorationView;
  pending: PendingRatingArm[];
  awaiting: boolean;
  onSubmitRound: (ratings: { candidateId: string; rating: TernaryRating }[]) => void;
  onFinalize: () => void;
}) {
  const [decisions, setDecisions] = useState<Map<string, FeedDecision>>(new Map());
  const [submittedRounds, setSubmittedRounds] = useState<Set<number>>(new Set());

  const decide = useCallback((id: string, d: FeedDecision) => {
    setDecisions((prev) => {
      const next = new Map(prev);
      next.set(id, d);
      return next;
    });
  }, []);

  // Flatten the buffer into an ordered queue: round asc, then FIT desc within a round.
  const queue = useMemo<FeedItem[]>(() => {
    const items: FeedItem[] = [];
    for (const arm of pending) {
      for (const c of arm.candidates) {
        items.push({ candidate: c, chips: arm.facetChips, roundIndex: arm.roundIndex });
      }
    }
    return items.sort(
      (a, b) =>
        a.roundIndex - b.roundIndex ||
        (b.candidate.judgement?.score ?? 0) - (a.candidate.judgement?.score ?? 0),
    );
  }, [pending]);

  const roundsInBuffer = useMemo(() => {
    const s = new Set<number>();
    for (const a of pending) s.add(a.roundIndex);
    return Array.from(s).sort((x, y) => x - y);
  }, [pending]);

  // Display unit = one arm's stratified triple. Show the OLDEST arm (round asc) that still
  // has an undecided candidate as a SET of cards; advance to the next arm when it's done.
  const activeArm = useMemo(
    () =>
      [...pending]
        .sort((a, b) => a.roundIndex - b.roundIndex)
        .find((arm) => arm.candidates.some((c) => !decisions.has(c.id))) ?? null,
    [pending, decisions],
  );
  // Keyboard focus = the first still-undecided card in the active arm.
  const focusedId = activeArm?.candidates.find((c) => !decisions.has(c.id))?.id ?? null;
  const decidedInBuffer = queue.filter((it) => decisions.has(it.candidate.id)).length;

  // Auto-submit the oldest fully-decided round, in order. Gate: only when parked and no
  // earlier submitted round is still buffered (its fold in flight) — so submits never race.
  useEffect(() => {
    if (!awaiting) return;
    const foldInFlight = roundsInBuffer.some((r) => submittedRounds.has(r));
    if (foldInFlight) return;
    for (const r of roundsInBuffer) {
      const cands = pending.filter((a) => a.roundIndex === r).flatMap((a) => a.candidates);
      if (!cands.every((c) => decisions.has(c.id))) break; // in order — stop at first incomplete
      const ratings = cands.flatMap((c) => {
        const d = decisions.get(c.id);
        return d && d !== "skip" ? [{ candidateId: c.id, rating: d }] : [];
      });
      setSubmittedRounds((prev) => new Set(prev).add(r));
      onSubmitRound(ratings);
      break;
    }
  }, [awaiting, decisions, pending, roundsInBuffer, submittedRounds, onSubmitRound]);

  // Keyboard: 1/2/3 = 👍/😐/👎, 0 or Space = skip — applied to the focused card, which
  // then auto-advances to the next undecided card in the arm's set.
  useEffect(() => {
    if (!focusedId) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable))
        return;
      const map: Record<string, FeedDecision> = { "1": "up", "2": "mid", "3": "down", "0": "skip" };
      const d = e.key === " " ? "skip" : map[e.key];
      if (!d) return;
      e.preventDefault();
      decide(focusedId, d);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focusedId, decide]);

  const steer = useMemo(() => summarizeSteer(view.rounds), [view.rounds]);
  const rankOf = (id: string) => Math.max(1, view.results.findIndex((r) => r.id === id) + 1);

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-primary/40 bg-primary/5 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <UserCheck size={16} className="text-primary" /> Avalie enquanto eu busco
          </div>
          <Button variant="outline" size="sm" onClick={onFinalize} disabled={!awaiting}>
            Concluir agora
          </Button>
        </div>
        <p className="mt-1 max-w-2xl text-xs leading-relaxed text-muted-foreground">
          Avalie os candidatos de cada abordagem — 👍 bom · 😐 indiferente · 👎 ruim (ou pule). Suas
          notas guiam a busca em tempo real, com mais peso que o avaliador automático, e eu continuo
          trazendo mais enquanto você avalia.
        </p>
        {steer && <p className="mt-2 text-xs font-medium text-primary/90">{steer}</p>}
      </div>

      {activeArm ? (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] text-muted-foreground">por que apareceu:</span>
            {activeArm.facetChips.map((chip) => (
              <span
                key={chip}
                className="rounded-full border border-border bg-muted/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground"
              >
                {chip}
              </span>
            ))}
          </div>
          {activeArm.candidates.map((c) => {
            if (!c.judgement) return null;
            const decision = decisions.get(c.id);
            return (
              <div
                key={c.id}
                className={cn(
                  "rounded-xl transition-shadow",
                  c.id === focusedId &&
                    "ring-2 ring-primary/50 ring-offset-2 ring-offset-background",
                )}
              >
                <CandidateCard
                  candidate={c}
                  fit={c.judgement}
                  rank={rankOf(c.id)}
                  actions={
                    <div className="flex items-center gap-1.5">
                      <TernaryRatingControl
                        value={decision && decision !== "skip" ? decision : undefined}
                        onChange={(r) => decide(c.id, r)}
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          decide(c.id, "skip");
                        }}
                        className={cn(
                          "h-8 rounded-md border px-2 text-[11px] transition-colors",
                          decision === "skip"
                            ? "border-border bg-muted/60 text-foreground"
                            : "border-border text-muted-foreground hover:bg-muted/50",
                        )}
                      >
                        Pular
                      </button>
                    </div>
                  }
                />
              </div>
            );
          })}
          <p className="text-center text-[11px] text-muted-foreground">
            Atalhos: <span className="font-mono">1</span> 👍 · <span className="font-mono">2</span>{" "}
            😐 · <span className="font-mono">3</span> 👎 · <span className="font-mono">0</span>{" "}
            pular
          </p>
        </div>
      ) : (
        <FeedWaiting awaiting={awaiting} />
      )}

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="tabular-nums">
          {decidedInBuffer}/{queue.length} avaliados nesta leva
        </span>
        <span className="tabular-nums">
          {view.candidatesExplored} na lista · {view.pagesUsed}/{view.budgetPages} págs
        </span>
      </div>
    </div>
  );
}

/** Between levas: the queue is momentarily empty but the run is still probing. */
function FeedWaiting({ awaiting }: { awaiting: boolean }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border px-6 py-8 text-center">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      <p className="text-sm font-medium">Buscando mais candidatos…</p>
      <p className="max-w-sm text-xs leading-relaxed text-muted-foreground">
        {awaiting
          ? "Você avaliou todos desta leva — já estou trazendo os próximos."
          : "Aprofundando as abordagens que você aprovou."}
      </p>
    </div>
  );
}

/** A one-line summary of what the recruiter's most recent ratings steered — the "the model
 *  is listening" payoff. Read off the last human-rated round: the verdict tally + which
 *  approaches it closed (real dormancy), else that the approved veins are being deepened. */
function summarizeSteer(rounds: ExplorationView["rounds"]): string | null {
  for (let i = rounds.length - 1; i >= 0; i -= 1) {
    const arms = rounds[i]?.arms ?? [];
    const rated = arms
      .flatMap((a) => a.candidates)
      .filter((c) => c.judgement?.humanRating !== undefined);
    if (rated.length === 0) continue;
    const up = rated.filter((c) => c.judgement?.humanRating === "up").length;
    const down = rated.filter((c) => c.judgement?.humanRating === "down").length;
    const closed = arms
      .filter((a) => a.dormantReason)
      .map((a) => a.facetChips[0])
      .filter((x): x is string => Boolean(x));
    const tally: string[] = [];
    if (up) tally.push(`${up} 👍`);
    if (down) tally.push(`${down} 👎`);
    let s = tally.length ? `Suas notas na última leva: ${tally.join(" · ")}.` : "";
    if (closed.length) s += ` Encerrei: ${closed.slice(0, 2).join(", ")}.`;
    else if (up > down) s += " Aprofundando os veios que você aprovou.";
    return s || null;
  }
  return null;
}

/** A collapsible peek at the shortlist growing during a live review — read-only (the full
 *  select-and-push-to-Level-Assessment surface appears once the run finishes). */
function PartialShortlist({ results }: { results: JudgedCandidate[] }) {
  const [open, setOpen] = useState(false);
  if (results.length === 0) return null;
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="inline-flex items-center gap-2 text-[13px] text-muted-foreground">
          <Layers size={14} className="text-muted-foreground" aria-hidden />
          Lista parcial —{" "}
          <span className="font-semibold tabular-nums text-foreground">{results.length}</span>{" "}
          candidatos até agora
        </span>
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary">
          {open ? "Ocultar" : "Ver lista"}
          <ChevronDown size={15} className={cn("transition-transform", open && "rotate-180")} />
        </span>
      </button>
      {open && (
        <div className="space-y-3 border-t border-border p-4">
          {results.map((c, i) =>
            c.judgement ? (
              <CandidateCard
                key={c.id}
                candidate={c}
                fit={c.judgement}
                rank={i + 1}
                actions={
                  c.judgement.humanRating ? (
                    <HumanVerdictBadge rating={c.judgement.humanRating} />
                  ) : undefined
                }
              />
            ) : null,
          )}
        </div>
      )}
    </div>
  );
}

/** The 👍 / 😐 / 👎 control. Three mutually-exclusive buttons; the active one is filled. */
function TernaryRatingControl({
  value,
  onChange,
  disabled,
}: {
  value?: TernaryRating;
  onChange: (rating: TernaryRating) => void;
  disabled?: boolean;
}) {
  const opts: { rating: TernaryRating; icon: typeof ThumbsUp; label: string; active: string }[] = [
    {
      rating: "up",
      icon: ThumbsUp,
      label: "Bom",
      active: "border-emerald-500 bg-emerald-500/15 text-emerald-600",
    },
    {
      rating: "mid",
      icon: Minus,
      label: "Indiferente",
      active: "border-amber-500 bg-amber-500/15 text-amber-600",
    },
    {
      rating: "down",
      icon: ThumbsDown,
      label: "Ruim",
      active: "border-rose-500 bg-rose-500/15 text-rose-600",
    },
  ];
  return (
    <div className="flex items-center gap-1">
      {opts.map(({ rating, icon: Icon, label, active }) => {
        const on = value === rating;
        return (
          <button
            key={rating}
            type="button"
            disabled={disabled}
            aria-pressed={on}
            aria-label={label}
            title={label}
            onClick={(e) => {
              e.stopPropagation();
              onChange(rating);
            }}
            className={cn(
              "flex h-8 w-8 items-center justify-center rounded-md border transition-colors disabled:opacity-50",
              on ? active : "border-border text-muted-foreground hover:bg-muted/50",
            )}
          >
            <Icon size={15} />
          </button>
        );
      })}
    </div>
  );
}

function ExploringView({ reopening = false }: { reopening?: boolean }) {
  return (
    <section className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card px-6 py-12 text-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      <p className="text-sm font-medium">
        {reopening ? "Carregando exploração salva…" : "Explorando e avaliando candidatos…"}
      </p>
      <p className="max-w-md text-sm leading-relaxed text-muted-foreground">
        {reopening
          ? "Recuperando a lista de candidatos já avaliados — sem custo adicional de busca."
          : "Testando combinações de filtros e avaliando os perfis de cada uma automaticamente. Os melhores candidatos aparecem aqui conforme a exploração avança."}
      </p>
    </section>
  );
}
